import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadCringeState, loginCringe, startCringeGame } from './cringeSlice';
import { useIsMobile } from './hooks';
import './cringe.css';

const isTrue = (v) => String(v || '').toLowerCase() === 'true';

/** Where the player belongs, given their saved progress (each PHP stage redirected on a filled stageN_score). */
export function routeForState(game) {
  if (!game) return '/stage1';
  if (game.gameover) return '/thankyou';
  return `/stage${game.nextStage}`;
}

/** ExtraMilePlay / company logo + Back (../actions-default.php in the PHP pages). */
export function CringeHeader() {
  const session = useSelector((s) => s.cringe.session);
  const logo = session?.companyLogoUrl || process.env.REACT_APP_EM_LOGO;
  const home = process.env.REACT_APP_BASE_URL;
  const backUrl = session?.backButtonRedirect || home;
  const fromApp = isTrue(session?.fromMobileApp);

  const onLogo = (e) => {
    if (!fromApp) return;
    e.preventDefault();
    window.GameStatus?.postMessage?.('navigate_back');
  };

  return (
    <header className="cr-header">
      <a href={home} onClick={onLogo}>
        {logo && <img src={logo} className="cr-logo" alt="Extramile Play" />}
      </a>
      {!fromApp && (
        <div className="cr-back-holder">
          <a href={backUrl}>
            <button type="button" className="cr-back-btn">Back</button>
          </a>
        </div>
      )}
    </header>
  );
}

/**
 * Full-screen page: background (desktop art, or the mobile art at <=768px like the PHP media
 * queries), header, login + progress. `stage` (1-5 or 'thankyou') checks once, when the page opens,
 * that this is where the player is up to and otherwise sends them on — as every PHP page did on load.
 * Stage 1 creates the player's `stages` row when it opens (stage1.php).
 */
export default function CringeShell({ bg, bgMob, bgColor, stage, className = '', children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const mobile = useIsMobile();
  const { session, game, loaded, authStatus, authError, starting, startTried, error } = useSelector((s) => s.cringe);
  const [ready, setReady] = useState(!stage);
  const checked = useRef(false);

  useEffect(() => {
    if (!session && authStatus === 'idle') dispatch(loginCringe());
  }, [session, authStatus, dispatch]);

  useEffect(() => {
    if (session && !loaded) dispatch(loadCringeState());
  }, [session, loaded, dispatch]);

  useEffect(() => {
    if (stage === 1 && session && loaded && !game && !starting && !startTried) dispatch(startCringeGame());
  }, [stage, session, loaded, game, starting, startTried, dispatch]);

  useEffect(() => {
    if (!stage || checked.current || !loaded) return;
    if (stage === 1 && !game) return; // wait for the row to be created
    checked.current = true;
    const here = stage === 'thankyou' ? '/thankyou' : `/stage${stage}`;
    const target = routeForState(game);
    if (target !== here) navigate({ pathname: target, search }, { replace: true });
    else setReady(true);
  }, [stage, loaded, game, navigate, search]);

  const image = mobile && bgMob ? bgMob : bg;
  const style = {
    ...(image ? { backgroundImage: `url(/cringe/img/${image})` } : {}),
    ...(bgColor ? { backgroundColor: bgColor } : {}),
  };

  let body = children;
  if (!session) {
    body =
      authStatus === 'failed' ? (
        <div className="cr-message">
          <p>{authError || 'Please launch Cringe who stole Christmas from ExtraMilePlay.'}</p>
          <a href={process.env.REACT_APP_BASE_URL}>Go to ExtraMilePlay</a>
        </div>
      ) : (
        <div className="cr-message"><p>Loading…</p></div>
      );
  } else if (!ready) {
    const failed = loaded && error && (stage !== 1 || (!game && startTried && !starting));
    body = (
      <div className="cr-message">
        <p>{failed ? error : 'Loading…'}</p>
      </div>
    );
  }

  return (
    <div className={`cr-page ${mobile ? 'is-mobile' : 'is-desktop'} ${className}`} style={style}>
      <CringeHeader />
      {body}
    </div>
  );
}
